// components/admin/JobCompletionApprovals.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, User, Award } from 'lucide-react';
import JobCompletionModal from '../logistics/JobCompletionModal';
import { useJobs } from '../../context/JobsContext';
import { useNotification } from '../../context/NotificationContext';

const JobCompletionApprovals = ({ groupId }) => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const jobsContext = useJobs();
  const { addNotification } = useNotification();

  useEffect(() => {
    // Safety check - ensure we have the context
    if (!jobsContext || !jobsContext.groupJobs) {
      console.warn('JobsContext not available or groupJobs is undefined');
      setPendingApprovals([]);
      return;
    }

    const { groupJobs } = jobsContext;
    
    // Get all jobs pending completion for this group
    const jobs = groupJobs[groupId] || [];
    const pending = jobs.filter(job => job.status === 'pending_completion');
    setPendingApprovals(pending);
  }, [jobsContext, groupId]);

  const handleApprove = (job) => {
    setSelectedJob(job);
    setShowApprovalModal(true);
  };

  const handleApprovalConfirm = async (approvalData) => {
    if (!jobsContext || !jobsContext.approveJobCompletion) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Job approval system not available',
        showToast: true
      });
      return;
    }

    try {
      await jobsContext.approveJobCompletion(selectedJob.id, approvalData);
      addNotification({
        type: 'success',
        title: 'Job Approved!',
        message: `Tokens distributed to ${selectedJob.claimedBy}`,
        showToast: true
      });
      setShowApprovalModal(false);
      setSelectedJob(null);
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Approval Failed',
        message: error.message,
        showToast: true
      });
    }
  };

  if (!jobsContext) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <Clock className="mx-auto text-gray-300 mb-3" size={48} />
        <p className="text-gray-600">Loading job approvals...</p>
      </div>
    );
  }

  if (pendingApprovals.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <Clock className="mx-auto text-gray-300 mb-3" size={48} />
        <p className="text-gray-600">No jobs pending approval</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <h3 className="font-medium text-yellow-800 mb-1">
          {pendingApprovals.length} Job{pendingApprovals.length > 1 ? 's' : ''} Awaiting Approval
        </h3>
        <p className="text-sm text-yellow-700">
          Review and approve completed jobs to release tokens to workers.
        </p>
      </div>

      {pendingApprovals.map(job => (
        <div key={job.id} className="bg-white border-2 border-yellow-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h4 className="font-semibold">{job.title}</h4>
              <div className="flex items-center text-sm text-gray-600 mt-1">
                <User size={14} className="mr-1" />
                <span>Worker: {job.claimedBy}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold">{job.tokenReward}</div>
              <div className="text-xs text-gray-600">tokens</div>
            </div>
          </div>

          {job.completionData && (
            <div className="bg-gray-50 rounded p-3 mb-3">
              <p className="text-sm text-gray-700">
                <strong>Worker Notes:</strong> {job.completionData.workerComments || 'None'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Completed: {new Date(job.completionData.completedAt).toLocaleString()}
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => handleApprove(job)}
              className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 flex items-center justify-center"
            >
              <CheckCircle size={16} className="mr-1" />
              Approve & Pay
            </button>
            <button
              className="px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50"
            >
              <XCircle size={16} />
            </button>
          </div>
        </div>
      ))}

      {selectedJob && (
        <JobCompletionModal
          isOpen={showApprovalModal}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedJob(null);
          }}
          job={selectedJob}
          onConfirm={handleApprovalConfirm}
          isAdmin={true}
        />
      )}
    </div>
  );
};

export default JobCompletionApprovals;